import { type Timeframe, TIMEFRAME_SECONDS, VENUES } from "@trader/shared/MarketData"
import { CreateAccount, type FillModelId } from "@trader/shared/Money"
import { Schema } from "effect"
import { localMidnight, timeZoneForCurrency } from "./format.ts"
import { INTRADAY_TIMEFRAMES } from "./orderTicket.ts"

// The New paper account form, without React: defaults per currency and the
// CreateAccount request it builds, decoded with the shared schema the server uses.

/** Currencies some venue trades in (an account in any other could never trade). */
export const ACCOUNT_CURRENCIES: ReadonlyArray<string> = [...new Set(Object.values(VENUES).map((venue) => venue.currency))]

/** Starting cash per currency: ₹10,00,000, $100,000, €100,000, ¥10,000,000. */
export const DEFAULT_CASH: Readonly<Record<string, string>> = {
  INR: "1000000",
  USD: "100000",
  EUR: "100000",
  JPY: "10000000"
}

export type ChargeChoice = "Zero" | "FyersNseEquity" | "Percent" | "PerOrder"

export interface NewAccountForm {
  readonly name: string
  readonly currency: string
  readonly startingCash: string
  readonly clock: "Realtime" | "Replay"
  readonly timeframe: Timeframe
  /** Replay start, a venue-local date "YYYY-MM-DD". */
  readonly from: string
  readonly charges: ChargeChoice
  /** Percent model: basis points of the fill value, and the minimum per fill. */
  readonly bps: string
  readonly minFee: string
  /** PerOrder model: a flat amount per fill. */
  readonly perFill: string
  readonly fillModel: FillModelId
  readonly squareOffTime: string
}

/** Fyers' NSE equity charges are rupee charges; other markets default to none. */
export const defaultCharges = (currency: string): ChargeChoice => (currency === "INR" ? "FyersNseEquity" : "Zero")

const DAY = 86_400

/** How far before the last stored bar a replay starts by default: about a screen of bars, and room to play. */
const REPLAY_LOOKBACK_DAYS: Readonly<Record<Timeframe, number>> = {
  "1m": 7,
  "3m": 10,
  "5m": 14,
  "15m": 30,
  "1h": 90,
  "4h": 180,
  "1D": 365
}

/** "YYYY-MM-DD" of an epoch-second time in UTC (replay starts are local midnights of that date). */
const isoDateOf = (epochSeconds: number) => new Date(epochSeconds * 1000).toISOString().slice(0, 10)

/**
 * The default replay start: inside the stored history of the chart's symbol at
 * that timeframe (`latestStored`, the open of its last stored bar), so the replay
 * has bars to play from the first step. Without it: the start of last year for
 * intraday timeframes (stored intraday history is short), the year before for 1D.
 */
export const defaultReplayFrom = (timeframe: Timeframe, latestStored: number | null, now: Date): string => {
  if (latestStored !== null) return isoDateOf(latestStored - REPLAY_LOOKBACK_DAYS[timeframe] * DAY)
  return `${now.getUTCFullYear() - (timeframe === "1D" ? 2 : 1)}-01-01`
}

export const defaultForm = (currency: string, timeframe: Timeframe, now: Date, latestStored: number | null = null): NewAccountForm => ({
  name: `Paper ${currency}`,
  currency,
  startingCash: DEFAULT_CASH[currency] ?? "100000",
  clock: "Replay",
  timeframe,
  from: defaultReplayFrom(timeframe, latestStored, now),
  charges: defaultCharges(currency),
  bps: "3",
  minFee: "0",
  perFill: "20",
  fillModel: "engine-parity",
  squareOffTime: "15:15"
})

/** Switching currency moves the cash and charges to that currency's defaults when they were untouched. */
export const withCurrency = (form: NewAccountForm, currency: string): NewAccountForm => ({
  ...form,
  currency,
  name: form.name === `Paper ${form.currency}` ? `Paper ${currency}` : form.name,
  startingCash: form.startingCash === (DEFAULT_CASH[form.currency] ?? "") ? DEFAULT_CASH[currency] ?? form.startingCash : form.startingCash,
  charges: form.charges === defaultCharges(form.currency) || (form.charges === "FyersNseEquity" && currency !== "INR")
    ? defaultCharges(currency)
    : form.charges
})

export type NewAccountField = "name" | "startingCash" | "from" | "charges" | "squareOffTime"
export type NewAccountErrors = Partial<Record<NewAccountField, string>>

export type NewAccountCheck =
  | { readonly ok: true; readonly request: CreateAccount }
  | { readonly ok: false; readonly errors: NewAccountErrors }

const decodeRequest = Schema.decodeUnknownResult(CreateAccount)
const cleanNumber = (text: string) => text.replace(/[,\s_]/g, "")

/**
 * The request the form describes, or what to fix. `now` is epoch seconds.
 * `fromTime` replaces the form's start date with an exact time (a replay started
 * from a picked bar starts at that bar's close, docs/design/0007).
 */
export const buildCreateAccount = (form: NewAccountForm, now: number, fromTime?: number): NewAccountCheck => {
  const errors: NewAccountErrors = {}
  const name = form.name.trim()
  if (name.length === 0) errors.name = "Give the account a name."
  else if (name.length > 64) errors.name = "At most 64 characters."

  const cash = cleanNumber(form.startingCash)
  if (!/^\d+(\.\d{1,2})?$/.test(cash) || /^0+(\.0+)?$/.test(cash)) errors.startingCash = "Enter an amount above zero."
  else if (cash.split(".")[0]!.length > 15) errors.startingCash = "That's more than this account can hold."

  let from: number | null = null
  if (form.clock === "Replay") {
    from = fromTime ?? localMidnight(form.from, timeZoneForCurrency(form.currency))
    if (from === null) errors.from = "Pick the date the replay starts."
    else if (from > now - TIMEFRAME_SECONDS[form.timeframe]) errors.from = "A replay starts in the past."
  }

  const chargeWire = (() => {
    switch (form.charges) {
      case "Zero":
        return { _tag: "Zero" }
      case "FyersNseEquity":
        return { _tag: "FyersNseEquity" }
      case "Percent":
        return { _tag: "Percent", bps: cleanNumber(form.bps), min: cleanNumber(form.minFee) || "0" }
      case "PerOrder":
        return { _tag: "PerOrder", amount: cleanNumber(form.perFill) }
    }
  })()
  if (form.charges === "FyersNseEquity" && form.currency !== "INR") errors.charges = "Fyers NSE charges apply to rupee accounts only."

  if (form.currency === "INR" && !/^([01]\d|2[0-3]):[0-5]\d$/.test(form.squareOffTime)) errors.squareOffTime = "Use HH:MM, e.g. 15:15."

  if (Object.keys(errors).length > 0) return { ok: false, errors }

  const decoded = decodeRequest({
    name,
    currency: form.currency,
    startingCash: cash,
    clock: form.clock === "Replay" ? { _tag: "Replay", timeframe: form.timeframe, from } : { _tag: "Realtime", timeframe: form.timeframe },
    config: {
      fillModel: form.fillModel,
      chargeModel: chargeWire,
      ...(form.currency === "INR" ? { squareOffTime: form.squareOffTime } : {})
    }
  })
  if (decoded._tag === "Failure") {
    return { ok: false, errors: form.charges === "Percent" || form.charges === "PerOrder" ? { charges: "Enter the charges as plain numbers." } : { name: "Check the form." } }
  }
  return { ok: true, request: decoded.success }
}

/** Intraday (MIS) works only on intraday timeframes: say so when the form picks a longer one. */
export const intradayNote = (form: NewAccountForm): string | null =>
  form.currency === "INR" && !INTRADAY_TIMEFRAMES.includes(form.timeframe)
    ? `Intraday (MIS) orders need 1h bars or shorter; ${form.timeframe} accounts trade CNC and NRML.`
    : null
